import { useState } from "react";

interface CategoryQuality {
  category: string;
  product_count: number;
  enriched_count: number;
  enrichment_pct: number;
  needs_review_count: number;
  needs_review_pct: number;
  avg_attributes: number;
  top_attributes: {
    name: string;
    count: number;
    coverage: number;
  }[];
}

interface DataQualityWheelProps {
  data: CategoryQuality[];
  onCategoryClick: (category: CategoryQuality) => void;
  centerLabel: string;
  centerStats?: {
    total_products: number;
    total_enriched: number;
    total_needs_review: number;
    overall_enrichment_pct: number;
  };
}

export const DataQualityWheel = ({ data, onCategoryClick, centerLabel, centerStats }: DataQualityWheelProps) => {
  const [hoveredSegment, setHoveredSegment] = useState<number | null>(null);

  // SVG dimensions
  const centerX = 700;
  const centerY = 700;
  const innerRadius = 120;
  const outerRadius = 400;
  const textRadius = 330;

  // Color based on enrichment percentage
  const getColorForEnrichment = (enrichmentPct: number, needsReviewPct: number) => {
    if (needsReviewPct > 20) return "rgb(239 68 68)"; // red - high review needed
    if (enrichmentPct >= 80) return "rgb(34 197 94)"; // green - excellent
    if (enrichmentPct >= 50) return "rgb(234 179 8)"; // yellow - good
    if (enrichmentPct >= 20) return "rgb(249 115 22)"; // orange - partial
    return "rgb(156 163 175)"; // gray - not enriched
  };

  // Simplify category name for display
  const getDisplayName = (category: string) => {
    const parts = category.split(' > ');
    return parts[parts.length - 1] || category;
  };

  const polarToCartesian = (angle: number, radius: number) => {
    const rad = (angle - 90) * Math.PI / 180;
    return {
      x: centerX + radius * Math.cos(rad),
      y: centerY + radius * Math.sin(rad)
    };
  };

  const totalSegments = data.length;
  const anglePerSegment = 360 / totalSegments;

  // Sort data by product_count descending for better visualization
  const sortedData = [...data].sort((a, b) => b.product_count - a.product_count);

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <svg
        viewBox="0 0 1400 1400"
        className="animate-zoom-in"
        style={{ width: "100%", height: "auto", maxWidth: "900px", userSelect: "none" }}
      >
        {/* Center circle */}
        <circle
          cx={centerX}
          cy={centerY}
          r={innerRadius}
          fill="hsl(var(--primary))"
          className="drop-shadow-lg"
        />

        {/* Center text */}
        <text
          x={centerX}
          y={centerY - 40}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="18"
          fontWeight="600"
          className="fill-primary-foreground"
        >
          {centerLabel}
        </text>
        {centerStats && (
          <>
            <text
              x={centerX}
              y={centerY}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="32"
              fontWeight="700"
              className="fill-primary-foreground"
            >
              {centerStats.overall_enrichment_pct}%
            </text>
            <text
              x={centerX}
              y={centerY + 30}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="12"
              className="fill-primary-foreground"
              opacity="0.8"
            >
              enriched
            </text>
            <text
              x={centerX}
              y={centerY + 55}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="11"
              className="fill-primary-foreground"
              opacity="0.7"
            >
              {centerStats.total_enriched.toLocaleString()}/{centerStats.total_products.toLocaleString()}
            </text>
          </>
        )}

        {/* Segments */}
        {sortedData.map((category, index) => {
          const angle = index * anglePerSegment;
          const midAngle = angle + anglePerSegment / 2;
          const startAngle = angle;
          const endAngle = angle + anglePerSegment - 1;

          const start = polarToCartesian(startAngle, outerRadius);
          const end = polarToCartesian(endAngle, outerRadius);
          const innerStart = polarToCartesian(startAngle, innerRadius + 40);
          const innerEnd = polarToCartesian(endAngle, innerRadius + 40);

          const largeArcFlag = anglePerSegment > 180 ? 1 : 0;

          const pathData = [
            `M ${start.x} ${start.y}`,
            `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
            `L ${innerEnd.x} ${innerEnd.y}`,
            `A ${innerRadius + 40} ${innerRadius + 40} 0 ${largeArcFlag} 0 ${innerStart.x} ${innerStart.y}`,
            'Z'
          ].join(' ');

          // Text positioning
          let rotation: number;
          let textAnchor: "start" | "end";

          if (midAngle > 180 && midAngle <= 360) {
            rotation = midAngle + 90;
            textAnchor = "start";
          } else {
            rotation = midAngle - 90;
            textAnchor = "end";
          }

          const textPos = polarToCartesian(midAngle, textRadius);
          const isHovered = hoveredSegment === index;
          const color = getColorForEnrichment(category.enrichment_pct, category.needs_review_pct);
          const displayName = getDisplayName(category.category);

          return (
            <g key={category.category} className="animate-wheel">
              {/* Colored segment */}
              <path
                d={pathData}
                fill={color}
                stroke="hsl(var(--background))"
                strokeWidth="2"
                className="cursor-pointer transition-all duration-300"
                style={{
                  filter: isHovered ? "brightness(1.2)" : "none",
                  opacity: isHovered ? 0.95 : 0.7
                }}
                onMouseEnter={() => setHoveredSegment(index)}
                onMouseLeave={() => setHoveredSegment(null)}
                onClick={() => onCategoryClick(category)}
              />

              {/* Category text */}
              {totalSegments <= 30 && (
                <text
                  x={textPos.x}
                  y={textPos.y}
                  fontSize={totalSegments > 20 ? "11" : "13"}
                  fill="hsl(var(--foreground))"
                  textAnchor={textAnchor}
                  transform={`rotate(${rotation}, ${textPos.x}, ${textPos.y})`}
                  className="font-medium cursor-pointer hover:font-bold transition-all"
                  opacity="0.85"
                  onMouseEnter={() => setHoveredSegment(index)}
                  onMouseLeave={() => setHoveredSegment(null)}
                  onClick={() => onCategoryClick(category)}
                  style={{ cursor: 'pointer', pointerEvents: 'all' }}
                >
                  {displayName.length > 25 ? displayName.substring(0, 22) + '...' : displayName}
                </text>
              )}

              {/* Enrichment percentage on segment */}
              {category.enrichment_pct > 0 && totalSegments <= 40 && (
                <text
                  x={polarToCartesian(midAngle, (innerRadius + 40 + outerRadius) / 2).x}
                  y={polarToCartesian(midAngle, (innerRadius + 40 + outerRadius) / 2).y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={totalSegments > 25 ? "10" : "12"}
                  fontWeight="bold"
                  fill="hsl(var(--background))"
                  className="cursor-pointer"
                  onMouseEnter={() => setHoveredSegment(index)}
                  onMouseLeave={() => setHoveredSegment(null)}
                  onClick={() => onCategoryClick(category)}
                  style={{ cursor: 'pointer', pointerEvents: 'all' }}
                >
                  {Math.round(category.enrichment_pct)}%
                </text>
              )}
            </g>
          );
        })}

        {/* Hover tooltip */}
        {hoveredSegment !== null && (() => {
          const category = sortedData[hoveredSegment];
          if (!category) return null;

          const angle = hoveredSegment * anglePerSegment + anglePerSegment / 2;
          const angleRad = (angle - 90) * Math.PI / 180;

          const tooltipRadius = 520;
          const tooltipSvgX = centerX + tooltipRadius * Math.cos(angleRad);
          const tooltipSvgY = centerY + tooltipRadius * Math.sin(angleRad);

          const w = 320;
          const h = 240 + Math.min(category.top_attributes.length * 18, 90);
          const x = tooltipSvgX - w / 2;
          const y = tooltipSvgY - h / 2;

          return (
            <foreignObject x={x} y={y} width={w} height={h}>
              <div className="bg-card border border-border rounded-lg shadow-xl p-4 animate-fade-in pointer-events-none">
                <div className="text-sm space-y-2">
                  <div className="font-semibold text-foreground text-base">
                    {getDisplayName(category.category)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {category.category}
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                    <div className="text-muted-foreground">
                      Products: <span className="font-medium text-foreground">{category.product_count.toLocaleString()}</span>
                    </div>
                    <div className={category.enrichment_pct >= 50 ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400"}>
                      Enriched: <span className="font-medium">{category.enrichment_pct}%</span>
                    </div>
                    <div className="text-muted-foreground">
                      AI Attrs: <span className="font-medium text-foreground">{category.avg_attributes || 0}</span> avg
                    </div>
                    <div className={category.needs_review_pct > 10 ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}>
                      Review: <span className="font-medium">{category.needs_review_count}</span> ({category.needs_review_pct}%)
                    </div>
                  </div>

                  {category.top_attributes.length > 0 && (
                    <div className="pt-2 border-t">
                      <div className="text-xs font-medium text-muted-foreground mb-1">
                        Top Attributes:
                      </div>
                      <div className="space-y-1">
                        {category.top_attributes.slice(0, 5).map((attr) => (
                          <div key={attr.name} className="flex justify-between text-xs">
                            <span className="text-foreground">{attr.name}</span>
                            <span className="text-muted-foreground">{attr.coverage}% coverage</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="text-xs text-muted-foreground italic pt-2 border-t">
                    Click to review this category
                  </div>
                </div>
              </div>
            </foreignObject>
          );
        })()}
      </svg>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-card border rounded-lg p-3 text-xs space-y-1">
        <div className="font-medium mb-2">Enrichment Status</div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: "rgb(34 197 94)" }} />
          <span>80%+ Enriched</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: "rgb(234 179 8)" }} />
          <span>50-79% Enriched</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: "rgb(249 115 22)" }} />
          <span>20-49% Enriched</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: "rgb(156 163 175)" }} />
          <span>&lt;20% Enriched</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: "rgb(239 68 68)" }} />
          <span>&gt;20% Needs Review</span>
        </div>
      </div>
    </div>
  );
};
